import React, { useEffect, useState } from "react";
import { Plus, Link2, X } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, StatusBadge, EmptyState } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { api, errMsg } from "../../lib/api";

export default function MasterServices() {
  const [services, setServices] = useState(null);
  const [munis, setMunis] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [open, setOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [form, setForm] = useState({ service_name: "", service_code: "", category: "Certificate", description: "" });
  const [assignForm, setAssignForm] = useState({ municipality_id: "", master_service_id: "" });
  const [deassign, setDeassign] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, m, a] = await Promise.all([
      api.get("/platform/master-services"),
      api.get("/platform/municipalities"),
      api.get("/platform/assignments"),
    ]);
    setServices(s.data);
    setMunis(m.data);
    setAssignments(a.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const createService = async (e) => {
    e.preventDefault();
    try {
      await api.post("/platform/master-services", form);
      toast.success("Service template created");
      setOpen(false);
      setForm({ service_name: "", service_code: "", category: "Certificate", description: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const assign = async (e) => {
    e.preventDefault();
    try {
      await api.post("/platform/assignments", assignForm);
      toast.success("Service assigned and synced to municipality");
      setAssignOpen(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const confirmDeassign = async () => {
    if (!deassign) return;
    setBusy(true);
    try {
      await api.delete(`/platform/assignments/${deassign.id}`);
      toast.success(`${deassign.service_name} de-assigned from ${deassign.municipality_name} — service taken offline in the ULB portal`);
      setDeassign(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (!services) return <Spinner />;

  return (
    <div>
      <PageHeader title="Master Services" subtitle="Service templates and municipality assignments">
        <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="assign-service-button"><Link2 className="h-4 w-4 mr-2" /> Assign to Municipality</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Assign service</DialogTitle></DialogHeader>
            <form onSubmit={assign} className="space-y-3">
              <div className="space-y-1">
                <Label>Municipality</Label>
                <Select value={assignForm.municipality_id} onValueChange={(v) => setAssignForm({ ...assignForm, municipality_id: v })}>
                  <SelectTrigger data-testid="assign-muni-select"><SelectValue placeholder="Select municipality" /></SelectTrigger>
                  <SelectContent>
                    {munis.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Service</Label>
                <Select value={assignForm.master_service_id} onValueChange={(v) => setAssignForm({ ...assignForm, master_service_id: v })}>
                  <SelectTrigger data-testid="assign-service-select"><SelectValue placeholder="Select service" /></SelectTrigger>
                  <SelectContent>
                    {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.service_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full" data-testid="assign-submit-button">Assign &amp; Sync</Button>
            </form>
          </DialogContent>
        </Dialog>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-service-button"><Plus className="h-4 w-4 mr-2" /> New Template</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">New service template</DialogTitle></DialogHeader>
            <form onSubmit={createService} className="space-y-3">
              <div className="space-y-1">
                <Label>Service Name</Label>
                <Input value={form.service_name} onChange={(e) => setForm({ ...form, service_name: e.target.value })} required data-testid="service-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Code</Label>
                  <Input value={form.service_code} onChange={(e) => setForm({ ...form, service_code: e.target.value.toUpperCase() })} required data-testid="service-code-input" />
                </div>
                <div className="space-y-1">
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Tax", "Certificate", "License", "Complaint", "Service"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
              </div>
              <Button type="submit" className="w-full" data-testid="service-submit-button">Create template</Button>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6" data-testid="services-grid">
        {services.map((s) => {
          const assigned = assignments.filter((a) => a.master_service_id === s.id);
          return (
            <Card key={s.id} className="shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-sm">{s.service_name}</p>
                    <p className="text-xs text-muted-foreground font-mono">{s.service_code}</p>
                  </div>
                  <Badge variant="secondary">{s.category}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{s.description}</p>
                {s.module_key && (
                  <p className="text-[11px] text-primary mt-1" data-testid={`service-module-hint-${s.service_code}`}>
                    Assigning activates the module for that municipality only
                  </p>
                )}
                <div className="mt-3 flex flex-wrap gap-1">
                  {assigned.length === 0 ? (
                    <span className="text-[11px] text-muted-foreground">Not assigned yet</span>
                  ) : assigned.map((a) => (
                    <Badge key={a.id} variant="outline" className="text-[10px] pr-1 gap-1">
                      {a.municipality_name}
                      <button type="button" title={`De-assign from ${a.municipality_name}`}
                        onClick={() => setDeassign(a)}
                        className="rounded-full hover:bg-destructive/10 hover:text-destructive p-0.5 -mr-0.5"
                        data-testid={`deassign-${s.service_code}-${a.municipality_id}`}>
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
        {services.length === 0 && <EmptyState title="No service templates" />}
      </div>

      <AlertDialog open={!!deassign} onOpenChange={(o) => !o && setDeassign(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">De-assign service from ULB?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deassign?.service_name}" will be removed from {deassign?.municipality_name} and taken offline
              in their citizen portal, so no new applications can be submitted. Already-submitted applications
              stay intact and remain processable. The municipal admin can re-enable it only after you assign
              the service again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deassign-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeassign} disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="deassign-confirm-button">
              {busy ? "Removing..." : "De-assign"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
